import { useState } from "react";
import { Lock, Plus } from "lucide-react";
import { toast } from "sonner";
import { useResource } from "../lib/hooks";
import { fmtDate } from "../lib/api";
import { PageHeader, Pill, statusTone } from "../components/Common";
import { RecordDialog } from "../components/RecordDialog";
import { ResourceManager } from "../components/ResourceManager";
import { SharePointPush } from "./RecordCentre";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const CLIENT_FIELDS = [
  { key: "name", label: "Full name", required: true }, { key: "dob", label: "Date of birth", type: "date" },
  { key: "email", label: "Email" }, { key: "phone", label: "Phone" },
  { key: "funding", label: "Funding", type: "select", options: ["Private", "NDIS - Plan managed", "NDIS - Self managed", "NDIS - Agency managed", "Medicare (MHCP)", "EAP", "Workcover"] },
  { key: "ndis_number", label: "NDIS number" }, { key: "referrer", label: "Referrer / GP" }, { key: "counsellor", label: "Assigned counsellor" },
  { key: "emergency_contact", label: "Emergency contact" }, { key: "status", label: "Status", type: "select", options: ["active", "waitlist", "closed"] },
  { key: "presenting_issues", label: "Presenting issues", type: "textarea" }, { key: "risk_notes", label: "Risk assessment", type: "textarea" },
];
const NOTE_FIELDS = [
  { key: "title", label: "Title", required: true }, { key: "format", label: "Format", type: "select", options: ["SOAP", "DAP", "BIRP", "Progress note", "Risk review"] },
  { key: "session_date", label: "Session date", type: "date" }, { key: "confidential", label: "Confidential (author & admins only)", type: "checkbox" },
  { key: "body", label: "Note", type: "textarea", rows: 8 },
];
const SESSION_FIELDS = [
  { key: "start", label: "Start", type: "datetime-local", required: true }, { key: "duration", label: "Duration (mins)", type: "number" },
  { key: "type", label: "Type", type: "select", options: ["Individual", "Couples", "Family", "Group", "Telehealth"] },
  { key: "status", label: "Status", type: "select", options: ["scheduled", "completed", "cancelled", "no-show"] },
  { key: "fee", label: "Fee (AUD)", type: "number" }, { key: "ndis_item", label: "NDIS support item no." },
];

function ClientDetail({ client }) {
  const notes = useResource("case_notes", { client_id: client.id });
  const [edit, setEdit] = useState(null);
  return (
    <div className="space-y-4" data-testid="client-detail">
      <div className="rounded-md border bg-card p-5">
        <div className="flex justify-between items-start">
          <div><div className="font-display text-2xl font-bold">{client.name}</div>
            <div className="text-xs text-muted-foreground mt-1">{client.funding} {client.ndis_number && `· NDIS ${client.ndis_number}`} · {client.counsellor || "Unassigned"}</div></div>
          <div className="flex gap-1"><Pill tone={statusTone(client.status)}>{client.status || "active"}</Pill><SharePointPush entity="clients" row={client} /></div>
        </div>
        {client.presenting_issues && <p className="text-sm mt-3 text-muted-foreground">{client.presenting_issues}</p>}
      </div>
      <Tabs defaultValue="notes">
        <TabsList><TabsTrigger value="notes" data-testid="tab-case-notes">Case notes</TabsTrigger><TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger></TabsList>
        <TabsContent value="notes" className="space-y-3">
          <Button size="sm" onClick={() => setEdit({ confidential: true, format: "SOAP", session_date: new Date().toLocaleDateString("en-CA") })} data-testid="new-case-note-btn"><Plus className="h-3.5 w-3.5 mr-1" />New case note</Button>
          {notes.items.map((n) => (
            <div key={n.id} className="rounded-md border bg-card p-4 cursor-pointer hover:border-primary/50" onClick={() => setEdit(n)} data-testid={`case-note-${n.id}`}>
              <div className="flex justify-between items-center"><span className="font-medium">{n.title}</span>
                <span className="flex items-center gap-2">{n.confidential && <Lock className="h-3.5 w-3.5 text-accent" />}<Pill tone="blue">{n.format}</Pill></span></div>
              <div className="text-xs text-muted-foreground font-mono mt-1">{n.session_date} · {n.author_name}</div>
              <p className="text-sm mt-2 whitespace-pre-wrap line-clamp-4">{n.body}</p>
            </div>
          ))}
          {!notes.items.length && <div className="text-sm text-muted-foreground">No case notes yet.</div>}
        </TabsContent>
        <TabsContent value="sessions">
          <ResourceManager resource="sessions" label="Session" testid="sessions" params={{ client_id: client.id }} fields={SESSION_FIELDS} defaults={{ client_id: client.id, status: "scheduled" }}
            columns={[{ key: "start", label: "Start", render: (r) => fmtDate(r.start) }, { key: "type", label: "Type" }, { key: "duration", label: "Mins" },
              { key: "fee", label: "Fee", render: (r) => (r.fee ? `A$${r.fee}` : "—") }, { key: "status", label: "Status", render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> }]} />
        </TabsContent>
      </Tabs>
      {edit && <RecordDialog open onOpenChange={(o) => !o && setEdit(null)} title={edit.id ? "Edit case note" : "New case note"} fields={NOTE_FIELDS} initial={edit}
        onSave={async (f) => { await notes.save({ ...f, client_id: client.id }, edit.id); toast.success("Case note saved"); }} />}
    </div>
  );
}

export default function Counsellor() {
  const clients = useResource("clients");
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState(null);
  const current = clients.items.find((c) => c.id === sel);
  return (
    <div>
      <PageHeader overline="Clinical" title="RCC Practice Suite" desc="Confidential client roster, sessions and structured case notes (SOAP/DAP/BIRP). NDIS & Medicare ready.">
        <Button onClick={() => setEdit({ status: "active" })} data-testid="new-client-btn"><Plus className="h-4 w-4 mr-1" />New client</Button>
      </PageHeader>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 rounded-md border bg-card/60 overflow-hidden" data-testid="client-roster">
          {!clients.items.length && <div className="p-6 text-sm text-muted-foreground">No clients yet.</div>}
          {clients.items.map((c) => (
            <div key={c.id} onClick={() => setSel(c.id)} data-testid={`client-${c.id}`}
              className={`p-4 border-b cursor-pointer flex justify-between items-center hover:bg-secondary ${sel === c.id ? "bg-secondary border-l-2 border-l-primary" : ""}`}>
              <div><div className="text-sm font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.funding || "—"}</div></div>
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEdit(c); }} data-testid={`edit-client-${c.id}`}>Edit</Button>
            </div>
          ))}
        </div>
        <div className="lg:col-span-8">{current ? <ClientDetail key={current.id} client={current} /> : <div className="rounded-md border p-10 text-center text-muted-foreground">Select a client to view sessions & case notes.</div>}</div>
      </div>
      {edit && <RecordDialog open onOpenChange={(o) => !o && setEdit(null)} title={edit.id ? "Edit client" : "New client"} fields={CLIENT_FIELDS} initial={edit}
        onSave={async (f) => { const c = await clients.save(f, edit.id); setSel(c.id); toast.success("Client saved"); }} />}
    </div>
  );
}
